import React, { useEffect } from "react";
import "./CookiePolicyPage.css";
import {
  CookiePolicy,
  CustomerContactData,
  PolicyBlock,
  PolicyIdentity,
  WebsiteCookie,
  WebsiteVendor,
} from "../components/Types/Configuration";
import { openConsentSettings } from "./consent";

interface CookiePolicyPageProps {
  policy: CookiePolicy | undefined;
  contactData: CustomerContactData;
  // The configuration row's timestamp: the default effective date.
  updatedAt: string | undefined;
  // Drives only the settings button: there is nothing to reopen without a banner.
  gtmActive: boolean;
}

// Finnish date for the visitor. A date-only value is calendar parts, not an
// instant: parsing "2026-09-15" as a Date makes it UTC midnight, which formats
// as the previous day for anyone west of UTC.
function finnishDate(value: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return `${Number(day)}.${Number(month)}.${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fi-FI").format(date);
}

// The backend fills what it can; an empty value is taken from what this page
// already has, so an older backend or a missing contact row still yields a
// complete document.
function resolveIdentity(policy: CookiePolicy | undefined, contactData: CustomerContactData, updatedAt: string | undefined): PolicyIdentity {
  const id = policy?.identity;
  const effective = id?.effectiveDate || updatedAt || "";
  return {
    companyName: id?.companyName || contactData.companyName || contactData.name || "",
    domain: id?.domain || (typeof window !== "undefined" ? window.location.host : ""),
    email: id?.email || contactData.email || "",
    effectiveDate: effective ? finnishDate(effective) : "",
  };
}

function fill(text: string, identity: PolicyIdentity): string {
  return text
    .split("{{companyName}}").join(identity.companyName)
    .split("{{domain}}").join(identity.domain)
    .split("{{email}}").join(identity.email)
    .split("{{effectiveDate}}").join(identity.effectiveDate);
}

// Block text is rendered as text, never as markup: dealers edit it freely.
function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const categoryLabel = (category: WebsiteVendor["category"]) => {
  switch (category) {
    case "analytics":
      return "Kävijämittaus";
    case "necessary":
      return "Välttämättömät";
    default:
      return "Markkinointi";
  }
};

const CookiePolicyPage: React.FC<CookiePolicyPageProps> = ({ policy, contactData, updatedAt, gtmActive }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const identity = resolveIdentity(policy, contactData, updatedAt);
  const blocks = policy?.blocks ?? [];
  const vendors = policy?.vendors ?? [];
  const necessary = policy?.necessary ?? [];

  const renderBlock = (block: PolicyBlock) => (
    <section key={block.id} aria-labelledby={`policy-${block.id}`}>
      <h2 id={`policy-${block.id}`}>{block.title}</h2>
      {paragraphs(fill(block.text, identity)).map((text, i) => (
        <p key={i}>{text}</p>
      ))}
    </section>
  );

  // The cookie table follows the last vendor description, or the categories
  // section when no vendor is declared.
  let tableAfter = blocks.findIndex((b) => b.id === "categories");
  blocks.forEach((b, i) => {
    if (b.kind === "vendor") tableAfter = i;
  });

  const vendorBlocks = blocks.filter((b) => b.kind === "vendor");

  return (
    <article className="policy-page">
      <h1>Evästekäytäntö</h1>
      {identity.effectiveDate && <p className="policy-effective">Voimassa {identity.effectiveDate} alkaen</p>}
      {blocks.map((block, index) => {
        if (block.kind === "vendor") {
          // Vendor descriptions render as one section with a sub-heading each.
          const first = vendorBlocks[0]?.id === block.id;
          return (
            <React.Fragment key={block.id}>
              {first && (
                <section aria-labelledby="policy-vendors">
                  <h2 id="policy-vendors">Käytetyt palvelut</h2>
                  {vendorBlocks.map((v) => (
                    <div key={v.id} className="policy-vendor">
                      <h3>{v.title}</h3>
                      {paragraphs(fill(v.text, identity)).map((text, i) => (
                        <p key={i}>{text}</p>
                      ))}
                    </div>
                  ))}
                </section>
              )}
              {index === tableAfter && renderTable(necessary, vendors)}
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={block.id}>
            {renderBlock(block)}
            {index === tableAfter && renderTable(necessary, vendors)}
          </React.Fragment>
        );
      })}
      {gtmActive && (
        <p>
          <button type="button" className="policy-settings-button" onClick={openConsentSettings}>
            Evästeasetukset
          </button>
        </p>
      )}
    </article>
  );
};

// A cookie in the always-on list either belongs to a service the dealer
// declared as such, or to the site itself (the consent record).
function necessarySetter(cookieName: string, vendors: WebsiteVendor[]): string {
  const owner = vendors.find(
    (vendor) => vendor.category === "necessary" && vendor.cookies.some((cookie) => cookie.name === cookieName),
  );
  return owner ? owner.provider || owner.name : "Tämä sivusto";
}

function renderTable(necessary: WebsiteCookie[], vendors: WebsiteVendor[]) {
  return (
    <section aria-labelledby="policy-cookies">
      <h2 id="policy-cookies">Käytetyt evästeet ja tallennustila</h2>
      <div className="policy-table-wrap">
        <table className="policy-table">
          <thead>
            <tr>
              <th scope="col">Nimi</th>
              <th scope="col">Luokka</th>
              <th scope="col">Asettaja</th>
              <th scope="col">Voimassaolo</th>
              <th scope="col">Käyttötarkoitus</th>
            </tr>
          </thead>
          <tbody>
            {necessary.map((cookie) => (
              <tr key={`necessary-${cookie.name}`}>
                <td>{cookie.name}</td>
                <td>Välttämättömät</td>
                <td>{necessarySetter(cookie.name, vendors)}</td>
                <td>{cookie.duration}</td>
                <td>{cookie.purpose}</td>
              </tr>
            ))}
            {vendors
              .filter((vendor) => vendor.category !== "necessary")
              .flatMap((vendor) =>
                vendor.cookies.map((cookie) => (
                <tr key={`${vendor.id}-${cookie.name}`}>
                  <td>{cookie.name}</td>
                  <td>{categoryLabel(vendor.category)}</td>
                  <td>{vendor.provider || vendor.name}</td>
                  <td>{cookie.duration}</td>
                    <td>{cookie.purpose}</td>
                  </tr>
                )),
              )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default CookiePolicyPage;
